import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { errMsg } from '../api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/jarvis-chat`;
const newSid = () => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const sidKey = 'jv-chat-session';

// Chat state + streaming + voice (record → transcribe → send; optional spoken replies).
export const useJarvisChat = () => {
  const { token } = useAuth();
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [sid, setSid] = useState(() => localStorage.getItem(sidKey) || newSid());
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem('jv-voice') === 'on');
  const [rec, setRec] = useState(null); // null | 'recording' | 'transcribing'
  const [secs, setSecs] = useState(0);
  const [playing, setPlaying] = useState(null);
  const media = useRef(null); const audio = useRef(null); const timer = useRef(null);

  useEffect(() => { localStorage.setItem(sidKey, sid); }, [sid]);
  useEffect(() => { localStorage.setItem('jv-voice', voiceOn ? 'on' : 'off'); }, [voiceOn]);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/status`, H).then((r) => setStatus(r.data)).catch(() => {});
    axios.get(`${API}/history`, { ...H, params: { session_id: sid } }).then((r) => setMsgs((m) => (m.length ? m : r.data.items || []))).catch(() => {});
  }, [token, sid]);

  const stop = () => { audio.current?.pause(); setPlaying(null); };
  const speak = useCallback(async (m) => {
    stop();
    try {
      setPlaying(m.id);
      const { data } = await axios.post(`${API}/speak`, { text: m.text.slice(0, 1500) }, H);
      const a = new Audio(`data:audio/mp3;base64,${data.audio_b64}`);
      audio.current = a; a.onended = () => setPlaying(null); await a.play();
    } catch (e) { setPlaying(null); toast.error(errMsg(e)); }
  }, [token]);

  const send = useCallback(async (text, { spoken } = {}) => {
    const t = (text || '').trim();
    if (!t || busy) return;
    const reply = { id: `tmp-${Date.now()}`, role: 'assistant', text: '', pending: true };
    setMsgs((m) => [...m, { id: `u-${Date.now()}`, role: 'user', text: t }, reply]);
    setBusy(true);
    const tmpId = reply.id; // fixed id: setMsgs updaters may run after `done`, so never compare against a mutated id
    const patch = (p) => setMsgs((m) => m.map((x) => (x.id === tmpId ? { ...x, ...p } : x)));
    let full = ''; let doneId = tmpId;
    try {
      const res = await fetch(`${API}/message`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ session_id: sid, text: t }) });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(typeof j.detail === 'string' ? j.detail : j.detail?.message || 'Jarvis is unavailable'); }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split('\n\n'); buf = parts.pop();
        parts.forEach((p) => {
          if (!p.startsWith('data: ')) return;
          const ev = JSON.parse(p.slice(6));
          if (ev.t === 'delta') { full += ev.c; patch({ text: full }); }
          if (ev.t === 'blocked') patch({ pending: false, blocked: true, text: ev.message });
          if (ev.t === 'error') patch({ pending: false, error: true, text: ev.message });
          if (ev.t === 'done') { patch({ pending: false, id: ev.id, actions: ev.actions }); doneId = ev.id; }
        });
      }
      if (full && (voiceOn || spoken)) speak({ id: doneId, text: full });
    } catch (e) { patch({ pending: false, error: true, text: e.message }); }
    setBusy(false);
  }, [busy, sid, token, voiceOn, speak]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((x) => x.stop()); clearInterval(timer.current);
        setRec('transcribing');
        try {
          const fd = new FormData(); fd.append('audio', new Blob(chunks, { type: mr.mimeType || 'audio/webm' }), 'voice.webm');
          const { data } = await axios.post(`${API}/transcribe`, fd, H);
          setRec(null);
          if (data.text) send(data.text, { spoken: true }); else toast.message("Didn't catch that. Try again.");
        } catch (e) { setRec(null); toast.error(errMsg(e)); }
      };
      media.current = mr; mr.start(); setSecs(0); setRec('recording');
      timer.current = setInterval(() => setSecs((s) => { if (s >= 59) mr.state === 'recording' && mr.stop(); return s + 1; }), 1000);
    } catch { toast.error('Microphone access is needed for voice. Check your browser permissions.'); }
  };
  const stopRec = () => media.current?.state === 'recording' && media.current.stop();
  const reset = async () => { stop(); await axios.delete(`${API}/session/${sid}`, H).catch(() => {}); setMsgs([]); setSid(newSid()); };

  return { msgs, busy, status, send, reset, voiceOn, setVoiceOn, rec, secs, startRec, stopRec, speak, stop, playing };
};
